import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ExportOptions = ({ onClose, onExport, isLoading = false }) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [includeRecommendations, setIncludeRecommendations] = useState(true);
  const [includeResources, setIncludeResources] = useState(true);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendCopy, setSendCopy] = useState(false);

  const exportFormats = [
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Complete formatted report with charts and recommendations',
      icon: 'FileText',
      size: '~2-3 MB'
    },
    {
      id: 'csv',
      name: 'Data Export (CSV)',
      description: 'Raw assessment data for analysis or record keeping',
      icon: 'Table',
      size: '~50 KB'
    },
    {
      id: 'json',
      name: 'JSON Data',
      description: 'Structured data format for technical use',
      icon: 'Code',
      size: '~25 KB'
    }
  ];

  const handleExport = () => {
    const exportOptions = {
      format: selectedFormat,
      includeRecommendations,
      includeResources,
      emailCopy: sendCopy ? emailAddress : null
    };
    
    onExport(selectedFormat, exportOptions);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-70 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-modal border border-border max-w-2xl w-full max-h-96 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-heading font-semibold text-text-primary">
              Export Your Results
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-text-secondary hover:text-text-primary transition-colors duration-150"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="space-y-6">
            {/* Format Selection */}
            <div>
              <h4 className="font-medium text-text-primary mb-3">Choose Export Format</h4>
              <div className="space-y-3">
                {exportFormats.map((format) => (
                  <label key={format.id} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="exportFormat"
                      value={format.id}
                      checked={selectedFormat === format.id}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Icon name={format.icon} size={16} color="var(--color-primary)" />
                        <span className="font-medium text-text-primary">{format.name}</span>
                        <span className="text-xs text-text-secondary">({format.size})</span>
                      </div>
                      <p className="text-sm text-text-secondary">{format.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Content Options */}
            {selectedFormat === 'pdf' && (
              <div>
                <h4 className="font-medium text-text-primary mb-3">Include in Report</h4>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeRecommendations}
                      onChange={(e) => setIncludeRecommendations(e.target.checked)}
                    />
                    <span className="text-sm text-text-primary">Detailed recommendations and action steps</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeResources}
                      onChange={(e) => setIncludeResources(e.target.checked)}
                    />
                    <span className="text-sm text-text-primary">Resource links and contact information</span>
                  </label>
                </div>
              </div>
            )}

            {/* Email Copy Option */}
            <div>
              <label className="flex items-center space-x-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={sendCopy}
                  onChange={(e) => setSendCopy(e.target.checked)}
                />
                <span className="font-medium text-text-primary">Email a copy to myself</span>
              </label>
              
              {sendCopy && (
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="Enter your email address"
                  className="form-input"
                />
              )}
            </div>

            {/* Privacy Notice */}
            <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Shield" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="font-medium text-primary mb-1">Privacy & Security</h5>
                  <p className="text-sm text-primary-600">
                    Your exported data is encrypted and secure. Email copies are sent through encrypted channels and automatically deleted from our servers after 24 hours.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-surface border border-border rounded-lg text-text-primary font-medium hover:bg-surface-secondary transition-all duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isLoading || (sendCopy && !emailAddress)}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Preparing Export...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Download" size={16} />
                    <span>Export Results</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;